import React from "react";
import { Form, Button, Badge } from "react-bootstrap";
import moment from "moment";

const ProjectOverviewTab = ({
  project,
  isEditing,
  saving,
  hasEditPermission,
  handleFieldChange,
  handleSave,
  handleCancel,
  setIsEditing,
  customers,
  customersLoading,
  selectedCustomerName,
  handleCustomerChange,
  projectTypesList,
  loadingProjectTypes,
  handleProjectTypeChangeForStatus,
  getProjectTypeDisplayName,
  projectStatusesList,
  loadingProjectStatuses,
  handleProjectStatusChangeForSubStatus,
  getProjectStatusDisplayName,
  projectSubStatusesList,
  loadingProjectSubStatuses,
  handleProjectSubStatusChange,
  getProjectSubStatusDisplayName,
  resourcesByOrg,
  resourcesByOrgLoading,
  getResourceNameById,
  teams,
  teamData,
  loadingManager,
  loadingSalesperson,
  getStatusBadge,
}) => {
  return (
    <div className="project-view-fade-in">
      <div className="project-overview-single-card">
        <div className="project-view-overview-grid">
          <div className="project-view-overview-row">
            <span className="project-view-overview-label">Project ID:</span>
            <span className="project-view-overview-value">
              {project?.projectId}
            </span>
            <span className="project-view-overview-label">Client:</span>
            {hasEditPermission() && isEditing ? (
              customersLoading ? (
                <div className="d-flex align-items-center">
                  <div
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <small className="text-muted">Loading customers...</small>
                </div>
              ) : (
                <Form.Select
                  value={project?.prClient || ""}
                  onChange={(e) => handleCustomerChange(e.target.value)}
                  size="sm"
                  className="project-view-editable-input"
                >
                  <option value="">Select a client</option>
                  {customers.map((customer) => (
                    <option
                      key={customer.customerCode}
                      value={customer.customerCode}
                    >
                      {customer.name} ({customer.customerCode})
                    </option>
                  ))}
                </Form.Select>
              )
            ) : (
              <span
                className={`project-view-overview-value ${hasEditPermission() ? "project-view-editable-field" : ""}`}
                onClick={() => hasEditPermission() && setIsEditing(true)}
                title={hasEditPermission() ? "Click to edit" : ""}
              >
                {selectedCustomerName || project?.prClient || "Not selected"}
              </span>
            )}
          </div>

          <div className="project-view-overview-row">
            <span className="project-view-overview-label">Project Type:</span>
            {hasEditPermission() && isEditing ? (
              loadingProjectTypes ? (
                <div className="d-flex align-items-center">
                  <div
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <small className="text-muted">Loading project types...</small>
                </div>
              ) : (
                <Form.Select
                  value={project?.prTypes || ""}
                  onChange={(e) =>
                    handleProjectTypeChangeForStatus(e.target.value)
                  }
                  size="sm"
                  className="project-view-editable-input"
                >
                  <option value="">Select Project Type</option>
                  {projectTypesList.map((type) => (
                    <option
                      key={type.projectTypeGUID}
                      value={type.projectTypeGUID}
                    >
                      {type.projectTypeDesc}
                    </option>
                  ))}
                </Form.Select>
              )
            ) : (
              <span
                className={`project-view-overview-value ${hasEditPermission() ? "project-view-editable-field" : ""}`}
                onClick={() => hasEditPermission() && setIsEditing(true)}
                title={hasEditPermission() ? "Click to edit" : ""}
              >
                {getProjectTypeDisplayName(project?.prTypes)}
              </span>
            )}
            <span className="project-view-overview-label">Category:</span>
            {hasEditPermission() && isEditing ? (
              <Form.Select
                value={project?.projectCategory || ""}
                onChange={(e) =>
                  handleFieldChange("projectCategory", e.target.value)
                }
                size="sm"
                className="project-view-editable-input"
              >
                <option value="">Select Category</option>
                <option value="Internal">Internal</option>
                <option value="External">External</option>
              </Form.Select>
            ) : (
              <span
                className={`project-view-overview-value ${hasEditPermission() ? "project-view-editable-field" : ""}`}
                onClick={() => hasEditPermission() && setIsEditing(true)}
                title={hasEditPermission() ? "Click to edit" : ""}
              >
                {project?.projectCategory || "-"}
              </span>
            )}
          </div>

          <div className="project-view-overview-row">
            <span className="project-view-overview-label">Tags:</span>
            {hasEditPermission() && isEditing ? (
              <Form.Control
                type="text"
                value={project?.prTags || ""}
                onChange={(e) => handleFieldChange("prTags", e.target.value)}
                size="sm"
                className="project-view-editable-input"
                placeholder="Comma separated tags"
              />
            ) : (
              <span
                className={`project-view-overview-value ${hasEditPermission() ? "project-view-editable-field" : ""}`}
                onClick={() => hasEditPermission() && setIsEditing(true)}
                title={hasEditPermission() ? "Click to edit" : ""}
              >
                {project?.prTags
                  ? project.prTags.split(",").map((tag) => (
                      <Badge key={tag} bg="secondary" className="me-1">
                        {tag.trim()}
                      </Badge>
                    ))
                  : "No tags"}
              </span>
            )}
            <span className="project-view-overview-label">Priority</span>
            {hasEditPermission() && isEditing ? (
              <Form.Select
                value={project?.projectPriority || ""}
                onChange={(e) =>
                  handleFieldChange("projectPriority", e.target.value)
                }
                size="sm"
                className="project-view-editable-input"
              >
                <option value="">Select Priority</option>
                <option value="Very High">Very High</option>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </Form.Select>
            ) : (
              <span
                className={`project-view-overview-value ${hasEditPermission() ? "project-view-editable-field" : ""}`}
                onClick={() => hasEditPermission() && setIsEditing(true)}
                title={hasEditPermission() ? "Click to edit" : ""}
              >
                {project?.projectPriority ? (
                  <Badge bg="secondary">{project.projectPriority}</Badge>
                ) : (
                  "No Priority"
                )}
              </span>
            )}
          </div>
        </div>

        <h3 className="project-view-overview-section-title mt-4">Timeline</h3>
        <div className="project-view-overview-grid">
          <div className="project-view-overview-row">
            <span className="project-view-overview-label">Start Date:</span>
            {hasEditPermission() && isEditing ? (
              <Form.Control
                type="date"
                value={
                  project?.prStartDate
                    ? moment(project.prStartDate).format("YYYY-MM-DD")
                    : ""
                }
                onChange={(e) =>
                  handleFieldChange("prStartDate", e.target.value)
                }
                size="sm"
                className="project-view-editable-input"
              />
            ) : (
              <span
                className={`project-view-overview-value ${hasEditPermission() ? "project-view-editable-field" : ""}`}
                onClick={() => hasEditPermission() && setIsEditing(true)}
              >
                {project?.prStartDate
                  ? moment(project.prStartDate).format("MMMM DD, YYYY")
                  : "Not set"}
              </span>
            )}
            <span className="project-view-overview-label">End Date:</span>
            {hasEditPermission() && isEditing ? (
              <Form.Control
                type="date"
                value={
                  project?.prEndDate
                    ? moment(project.prEndDate).format("YYYY-MM-DD")
                    : ""
                }
                onChange={(e) => handleFieldChange("prEndDate", e.target.value)}
                size="sm"
                className="project-view-editable-input"
              />
            ) : (
              <span
                className={`project-view-overview-value ${hasEditPermission() ? "project-view-editable-field" : ""}`}
                onClick={() => hasEditPermission() && setIsEditing(true)}
              >
                {project?.prEndDate
                  ? moment(project.prEndDate).format("MMMM DD, YYYY")
                  : "Not set"}
              </span>
            )}
          </div>
          <div className="project-view-overview-row">
            <span className="project-view-overview-label">Duration:</span>
            <span className="project-view-overview-value">
              {project?.prStartDate && project?.prEndDate
                ? moment(project.prEndDate).diff(
                    moment(project.prStartDate),
                    "days",
                  )
                : 0}{" "}
              days
            </span>
            <span className="project-view-overview-label">Days Remaining:</span>
            <span className="project-view-overview-value">
              {project?.prEndDate
                ? Math.max(0, moment(project.prEndDate).diff(moment(), "days"))
                : 0}{" "}
              days
            </span>
          </div>
        </div>

        <h3 className="project-view-overview-section-title mt-4">
          Team & Contact
        </h3>
        <div className="project-view-overview-grid">
          <div className="project-view-overview-row">
            <span className="project-view-overview-label">
              Project Manager:
            </span>
            {hasEditPermission() && isEditing ? (
              resourcesByOrgLoading ? (
                <div className="d-flex align-items-center">
                  <div
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <small className="text-muted">Loading resources...</small>
                </div>
              ) : (
                <Form.Select
                  value={project?.currentProjectManager || ""}
                  onChange={(e) =>
                    handleFieldChange("currentProjectManager", e.target.value)
                  }
                  size="sm"
                  className="project-view-editable-input"
                >
                  <option value="">Select Project Manager</option>
                  {resourcesByOrg.map((resource) => (
                    <option
                      key={resource.resourceId}
                      value={resource.resourceId}
                    >
                      {resource.name} ({resource.resourceId})
                    </option>
                  ))}
                </Form.Select>
              )
            ) : (
              <span
                className={`project-view-overview-value ${hasEditPermission() ? "project-view-editable-field" : ""}`}
                onClick={() => hasEditPermission() && setIsEditing(true)}
              >
                {loadingManager
                  ? "Loading..."
                  : getResourceNameById(project?.currentProjectManager) ||
                    "Not assigned"}
              </span>
            )}
            <span className="project-view-overview-label">Team Assigned:</span>
            {hasEditPermission() && isEditing ? (
              <Form.Select
                value={project?.teamId || ""}
                onChange={(e) => {
                  const selectedTeam = teams.find(
                    (team) => team.teamId === e.target.value,
                  );
                  handleFieldChange("teamId", e.target.value);
                  handleFieldChange(
                    "teamAssigned",
                    selectedTeam ? selectedTeam.teamName : "",
                  );
                }}
                size="sm"
                className="project-view-editable-input"
              >
                <option value="">Select a team</option>
                {teams.map((team) => (
                  <option key={team.teamId} value={team.teamId}>
                    {team.teamName}
                  </option>
                ))}
              </Form.Select>
            ) : (
              <span
                className={`project-view-overview-value ${hasEditPermission() ? "project-view-editable-field" : ""}`}
                onClick={() => hasEditPermission() && setIsEditing(true)}
              >
                {teamData?.teamName || project?.teamAssigned || "Not assigned"}
              </span>
            )}
          </div>
          <div className="project-view-overview-row">
            <span className="project-view-overview-label">Salesperson:</span>
            {hasEditPermission() && isEditing ? (
              resourcesByOrgLoading ? (
                <div className="d-flex align-items-center">
                  <div
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <small className="text-muted">Loading resources...</small>
                </div>
              ) : (
                <Form.Select
                  value={project?.salespersonCode || ""}
                  onChange={(e) =>
                    handleFieldChange("salespersonCode", e.target.value)
                  }
                  size="sm"
                  className="project-view-editable-input"
                >
                  <option value="">Select Salesperson</option>
                  {resourcesByOrg.map((resource) => (
                    <option
                      key={resource.resourceId}
                      value={resource.resourceId}
                    >
                      {resource.name} ({resource.resourceId})
                    </option>
                  ))}
                </Form.Select>
              )
            ) : (
              <span
                className={`project-view-overview-value ${hasEditPermission() ? "project-view-editable-field" : ""}`}
                onClick={() => hasEditPermission() && setIsEditing(true)}
              >
                {loadingSalesperson
                  ? "Loading..."
                  : getResourceNameById(project?.salespersonCode) ||
                    "Not assigned"}
              </span>
            )}
          </div>
          <div className="project-view-overview-row">
            <span className="project-view-overview-label">Status:</span>
            {hasEditPermission() && isEditing ? (
              loadingProjectStatuses ? (
                <div className="d-flex align-items-center">
                  <div
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <small className="text-muted">Loading statuses...</small>
                </div>
              ) : (
                <Form.Select
                  value={project?.projectStatus || ""}
                  onChange={(e) =>
                    handleProjectStatusChangeForSubStatus(e.target.value)
                  }
                  size="sm"
                  className="project-view-editable-input"
                  disabled={!project?.prTypes}
                >
                  <option value="">
                    {!project?.prTypes
                      ? "Select Project Type first"
                      : "Select Status"}
                  </option>
                  {projectStatusesList.map((status) => (
                    <option
                      key={status.projectStatusGUID}
                      value={status.projectStatusGUID}
                    >
                      {status.projectStatusDesc}
                    </option>
                  ))}
                </Form.Select>
              )
            ) : (
              <span
                className={`project-view-overview-value ${hasEditPermission() ? "project-view-editable-field" : ""}`}
                onClick={() => hasEditPermission() && setIsEditing(true)}
              >
                <span
                  className={`project-status-badge ${getStatusBadge(project?.projectStatus)}`}
                >
                  {getProjectStatusDisplayName(project?.projectStatus)}
                </span>
              </span>
            )}
            <span className="project-view-overview-label">Sub Status:</span>
            {hasEditPermission() && isEditing ? (
              loadingProjectSubStatuses ? (
                <div className="d-flex align-items-center">
                  <div
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <small className="text-muted">Loading sub-statuses...</small>
                </div>
              ) : (
                <Form.Select
                  value={project?.projectSubStatus || ""}
                  onChange={(e) => handleProjectSubStatusChange(e.target.value)}
                  size="sm"
                  className="project-view-editable-input"
                  disabled={!project?.projectStatus}
                >
                  <option value="">
                    {!project?.projectStatus
                      ? "Select Status first"
                      : "Select Sub Status"}
                  </option>
                  {projectSubStatusesList.map((subStatus) => (
                    <option
                      key={subStatus.projectSubStatusGUID}
                      value={subStatus.projectSubStatusGUID}
                    >
                      {subStatus.projectSubStatusDesc}
                    </option>
                  ))}
                </Form.Select>
              )
            ) : (
              <span
                className={`project-view-overview-value ${hasEditPermission() ? "project-view-editable-field" : ""}`}
                onClick={() => hasEditPermission() && setIsEditing(true)}
              >
                {getProjectSubStatusDisplayName(project?.projectSubStatus)}
              </span>
            )}
          </div>
        </div>

        <h3 className="project-view-overview-section-title mt-4">
          Additional Info
        </h3>
        <div className="project-view-overview-grid">
          <div className="project-view-overview-row">
            <span className="project-view-overview-label">Comments:</span>
            {hasEditPermission() && isEditing ? (
              <Form.Control
                as="textarea"
                rows={3}
                value={project?.comments || ""}
                onChange={(e) => handleFieldChange("comments", e.target.value)}
                className="project-view-editable-input"
                placeholder="Add comments..."
              />
            ) : (
              <span
                className={`project-view-overview-value ${hasEditPermission() ? "project-view-editable-field" : ""}`}
                onClick={() => hasEditPermission() && setIsEditing(true)}
              >
                {project?.comments || "No comments"}
              </span>
            )}
          </div>
        </div>

        {hasEditPermission() && isEditing && (
          <div className="project-view-edit-controls mt-3 d-flex gap-2 justify-content-end">
            <Button
              variant="success"
              size="sm"
              onClick={handleSave}
              disabled={saving}
            >
              Save
            </Button>
            <Button
              variant="outline-light"
              size="sm"
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectOverviewTab;
